import React, {FC, Fragment, ReactElement} from 'react';
import {PageHeader} from "../../components/PageHeader";
import {Col, Row} from "antd";
import {GUTTER} from "../../constants";
import {BookOutlined, UserOutlined, ClockCircleOutlined} from "@ant-design/icons";
import {IconCard} from "../../components/IconCard";
import {useQuery} from "react-query";
import {ExerciseDashboardViews, OrganisationOverview} from "../../types";
import {useOrganisation} from "../../layout/OrganisationInformationProvider";
import {AttributePerformanceColumnChartCard} from "../Products/components/AttributePerformanceColumnChartCard";
import {ActivityRendererPerformanceDataTableCard} from "./components/ActivityRendererPerformanceDataTableCard";
import {FilterPrefixProvider} from "../../components/DataTable/Filters/FilterPrefixProvider";
import {LabelsProvider, useLabels} from "../../hooks/Labels";
import {ActivityRendererOverviewCardMapping} from "../../components/DashboardViews/OverviewCards";
import { StudentEngagementChartCard } from './components/StudentEngagementChartCard';
import { formatMinutesDuration } from '../../helpers';
import isNumeric from 'antd/lib/_util/isNumeric';
import {PreserveQueryLink as Link} from "../../components/PreserveQueryLink";
import {NotEnoughUsage} from "../../components/NotEnoughUsage";
import {hasEnoughUsage} from "../../hooks/AnalyticsAccess";


export const OverviewPage: FC = (): ReactElement => {
    const {products, minimum_students_for_analytics} = useOrganisation();
    const {t} = useLabels();
    const {data, isLoading} = useQuery<OrganisationOverview>('/reports/overview');
    const canAccessAnalytics = hasEnoughUsage(data?.students, minimum_students_for_analytics);

    return (
        <Fragment>
            <PageHeader title='Overview'/>
            <Row gutter={GUTTER}>
                <Col xl={6} xs={24} style={{display: 'block'}}>
                    <Row gutter={GUTTER}>
                        <Col xs={24}>
                            <IconCard
                                title={t('student_pl')}
                                isLoading={isLoading}
                                value={data?.students}
                                icon={<UserOutlined />}
                                href='/students'
                            />
                        </Col>
                        <Col xs={24}>
                            <IconCard
                                title={t('course_pl')}
                                isLoading={isLoading}
                                value={data?.courses}
                                icon={<BookOutlined />}
                                href='/courses'
                            />
                        </Col>
                        <Col xs={24}>
                            <IconCard
                                isLoading={isLoading}
                                title='Monthly Active Users'
                                value={data?.active_users_past_month}
                                icon={<UserOutlined />}
                            />
                        </Col>
                        <Col xs={24}>
                            <IconCard
                                isLoading={isLoading}
                                title={t('average_time_spent_title')}
                                tooltip={`per ${t('activity', false)} in the past 30 days`}
                                value={isNumeric(data?.avg_completion_time_past_month_minutes) ? formatMinutesDuration(data!.avg_completion_time_past_month_minutes) : 'N/A'}
                                icon={<ClockCircleOutlined />}
                            />
                        </Col>
                    </Row>
                </Col>
                <Col xl={18} xs={24}>
                    {isLoading
                        ? null
                        : canAccessAnalytics
                            ? <FilterPrefixProvider prefix='engagement'>
                                <StudentEngagementChartCard/>
                            </FilterPrefixProvider>
                            : <NotEnoughUsage/>}
                </Col>
            </Row>
            {canAccessAnalytics && products && products.map(product => {
                const renderers = product.activity_renderers.filter(renderer =>
                    ExerciseDashboardViews.includes(renderer.dashboard_view)
                );
                const shouldUseRendererView
                    = renderers.length === 1 && (renderers[0].dashboard_view in ActivityRendererOverviewCardMapping)
                return (
                    <div key={product.id} style={{marginTop: GUTTER[0]}}>
                        <Link to={`/${product.slug}`} style={{fontSize: 36, fontWeight: 400}}>{product.name}</Link>
                        <Row gutter={GUTTER} style={{marginTop: GUTTER[0]}}>
                            <Col xl={10} xs={24}>
                                <AttributePerformanceColumnChartCard attributeMeta={product.primary_attribute_meta}/>
                            </Col>
                            <Col xl={14} xs={24}>
                                {
                                    shouldUseRendererView
                                    ? <LabelsProvider activityRenderer={renderers[0].id}>
                                        {ActivityRendererOverviewCardMapping[renderers[0].dashboard_view]}
                                    </LabelsProvider>
                                    : <ActivityRendererPerformanceDataTableCard product={product}/>
                                }
                            </Col>
                        </Row>
                    </div>
                )
            })}
        </Fragment>
    );
};
